// ═══════════════════════════════════════════════════════════
// Картка задачі в бічній панелі і рядок у списку.
//
// Споживачів троє — сторінка «Задачі», блок «Мій день» і вкладка в картці
// клієнта. Три копії тієї самої картки розійшлись би через місяць, і
// рядок «Взяти в роботу» поводився б по-різному залежно від того, звідки
// його натиснули.
// ═══════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiFetch } from '../../services/api';
import { uploadFile } from '../../services/uploadImage';
import { CompleteTaskModal, RescheduleModal, CancelTaskModal, BlockTaskModal } from './TaskModals';
import {
  STATUS_LABEL, PRIORITY_LABEL, TYPE_LABEL, OUTCOME_LABEL, RECURRENCE_LABEL,
  isOpenStatus, toDateInput, fmtDue, fmtWhen,
} from './taskVocab';

export function TaskRow({ task, onOpen, active }) {
  return (
    <button className={`tasks-row${active ? ' active' : ''}`} onClick={onOpen}>
      <span className={`tasks-prio tasks-prio--${task.priority}`} aria-hidden="true" />
      <span className={`tasks-status tasks-status--${task.status}`}>{STATUS_LABEL[task.status]}</span>
      <span className="tasks-title">
        {task.docNumber ? <span className="doc-num">#{task.docNumber}</span> : null}
        {task.title}
        {task.type === 'sale' && <span className="tasks-badge">Продаж</span>}
        {task.projectId && <span className="tasks-badge tasks-badge--project" title="У проєкті">Проєкт</span>}
        {task.blocked && (
          <span className="tasks-badge tasks-badge--blocked" title={`Блокує ${task.blocked.byName || task.blocked.external || ''}`}>
            Заблокована
          </span>
        )}
      </span>
      <span className="tasks-customer">{task.customerName || ''}</span>
      <span className="tasks-assignee">
        <span>{task.assigneeName || ''}</span>
        {task.createdBy?.name && task.createdBy.uid !== task.assigneeUid && (
          <span className="tasks-by" title={`Поставив: ${task.createdBy.name}`}>від {task.createdBy.name}</span>
        )}
      </span>
      <span className={`tasks-due${task.isOverdue ? ' overdue' : ''}`}>{fmtDue(task.dueAt)}</span>
    </button>
  );
}

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const fmtSize = (n) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} МБ` : `${Math.max(1, Math.round(n / 1024))} КБ`);

export function TaskPanel({ task, onClose, onPatch, onReload, onEdit }) {
  const { currentUser, hasPermission, tenantId } = useAuth();
  const toast = useToast();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState(false);     // перенесення дедлайну
  const [cancelling, setCancelling] = useState(false);
  const [blocking, setBlocking] = useState(false);     // «блокує хтось»

  // Список приходить без історії (comments === null) — вона важка й
  // потрібна лише тут. Довантажуємо один раз при відкритті; рядок у
  // списку теж отримує повну задачу через onPatch.
  const needsHistory = task.comments == null;
  useEffect(() => {
    if (!needsHistory) return undefined;
    let cancelled = false;
    apiFetch(`/api/analytics/tasks-index?id=${encodeURIComponent(task.id)}&full=1&page_size=1`)
      .then(r => { if (!cancelled && r.items?.[0]) onPatch(r.items[0]); })
      .catch(() => { /* панель працює і без історії */ });
    return () => { cancelled = true; };
  }, [needsHistory, task.id, onPatch]);

  const call = async (url, body, okMsg) => {
    setBusy(true);
    try {
      const r = await apiFetch(url, { method: 'POST', body });
      onPatch(r.task);
      if (okMsg) toast.success(okMsg);
      return r;
    } catch (err) {
      // Сервер повертає читабельне пояснення для 409 і 422 — показуємо його,
      // а не загальне "щось пішло не так".
      toast.error(err?.body?.message || err?.message || 'Не вдалося виконати дію');
      if (err?.status === 409 && onReload) onReload();
      return null;
    } finally {
      setBusy(false);
    }
  };

  const move = (status) => call('/api/tasks/status', { id: task.id, status });

  // Чекліст надсилаємо масивом цілком, на відміну від коментарів. Тут це
  // прийнятно: пункти править той, хто задачу виконує, і одночасне
  // клацання двома людьми — рідкість. Коментарі ж пишуть одночасно
  // регулярно, тому вони дописуються на боці бази.
  const toggleCheck = (index) => {
    const next = task.checklist.map((c, i) => (i === index ? { ...c, isDone: !c.isDone, doneAt: !c.isDone ? Date.now() : null } : c));
    return call('/api/tasks/update', { id: task.id, checklist: next });
  };

  const addComment = async () => {
    const body = comment.trim();
    if (!body) return;
    const r = await call('/api/tasks/comment', { id: task.id, body });
    if (r) setComment('');
  };

  // Файли: байти — прямо у Storage з браузера (як документи клієнта),
  // метадані — через API, який дописує їх до задачі на боці бази.
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const addFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) { toast.error('Файл більший за 15 МБ'); return; }
    setUploading(true);
    try {
      const safe = file.name.replace(/[^\w.-]+/g, '_').slice(0, 80) || 'file';
      const path = `tenants/${tenantId}/attachments/tasks/${task.id}/${Date.now()}_${safe}`;
      const url = await uploadFile(file, path);
      await call('/api/tasks/files', {
        id: task.id, action: 'add',
        file: { name: file.name, url, path, size: file.size, mime: file.type || null },
      }, 'Файл додано');
    } catch (err) {
      toast.error(err?.message || 'Не вдалося завантажити файл');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const removeFile = (f) => {
    if (!window.confirm(`Прибрати файл «${f.name}»?`)) return;
    return call('/api/tasks/files', { id: task.id, action: 'remove', fileId: f.id }, 'Файл прибрано');
  };

  const isOpen = isOpenStatus(task.status);
  // Та сама умова, що й на сервері: своя задача або право правити чужі.
  // Дублюється свідомо — щоб не показувати кнопку, яка гарантовано дасть
  // 403. Остаточне рішення все одно за сервером.
  const canEdit = hasPermission('tasks_edit_all')
    || task.assigneeUid === currentUser.uid
    || task.createdBy?.uid === currentUser.uid;

  return (
    <>
      <div className="tasks-panel-backdrop" onClick={onClose} />
      <aside className="tasks-panel">
        <div className="tasks-panel-head">
          <div>
            <div className="tasks-panel-meta">
              {TYPE_LABEL[task.type]} · {PRIORITY_LABEL[task.priority]}
              {task.docNumber ? ` · #${task.docNumber}` : ''}
            </div>
            <h2>{task.title}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="tasks-panel-body">
          {/* Запит на перенесення — над усім іншим: доки на нього не
              відповіли, задача лишається простроченою, і це найперше, що
              постановник має побачити, відкривши картку. */}
          {task.rescheduleRequest && (
            <RescheduleRequestBanner
              task={task} busy={busy}
              onAnswer={(action) => call('/api/tasks/reschedule', { id: task.id, action })}
            />
          )}

          {/* Плашка блокування видима всім, хто бачить задачу, — зокрема
              тому, через кого вона стоїть: він не виконавець і не
              постановник, але кнопка «Я зробив» саме для нього. */}
          {task.blocked && isOpen && (
            <BlockedBanner
              task={task} busy={busy}
              isBlocker={task.blocked.byUid === currentUser.uid}
              canRelease={canEdit || task.blocked.byUid === currentUser.uid}
              onRelease={() => call('/api/tasks/block', { id: task.id, action: 'release' }, 'Блокування знято')}
            />
          )}

          {task.description && <p className="tasks-panel-desc">{task.description}</p>}

          <dl className="tasks-facts">
            <dt>Статус</dt><dd><span className={`tasks-status tasks-status--${task.status}`}>{STATUS_LABEL[task.status]}</span></dd>
            <dt>Виконавець</dt><dd>{task.assigneeName || '—'}</dd>
            <dt>Дедлайн</dt><dd className={task.isOverdue ? 'overdue' : ''}>{task.dueAt ? fmtWhen(task.dueAt) : '—'}</dd>
            {task.customerName && (<><dt>Клієнт</dt><dd>{task.customerName}</dd></>)}
            {task.type === 'sale' && (
              <><dt>Очікувана сума</dt><dd>{task.expectedAmount ? `${task.expectedAmount} ₴` : '—'}</dd></>
            )}
            {task.outcome && (
              <><dt>Результат</dt><dd>
                {OUTCOME_LABEL[task.outcome]}
                {task.outcomeAmount ? ` · ${task.outcomeAmount} ₴` : ''}
              </dd></>
            )}
            {task.recurrenceRule && (
              <><dt>Повторюється</dt><dd>{RECURRENCE_LABEL[task.recurrenceRule] || task.recurrenceRule}</dd></>
            )}
            {/* «Нагадати мені» — це planned_for на майбутній день: у той день
                задача спливає в «Моєму дні». Нової сутності не треба. */}
            {isOpen && canEdit && (
              <>
                <dt>Нагадати мені</dt>
                <dd className="tasks-remind">
                  {task.plannedFor ? (
                    <>
                      {fmtDue(task.plannedFor)}
                      <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                        onClick={() => call('/api/tasks/update', { id: task.id, plannedFor: null })}>
                        Скинути
                      </button>
                    </>
                  ) : (
                    <input type="date" min={toDateInput(Date.now())} disabled={busy}
                      onChange={e => {
                        if (!e.target.value) return;
                        call('/api/tasks/update',
                          { id: task.id, plannedFor: new Date(`${e.target.value}T09:00`).getTime() },
                          'Спливе в «Моєму дні» цього дня');
                      }} />
                  )}
                </dd>
              </>
            )}
            <dt>Створив</dt><dd>{task.createdBy?.name || '—'}, {fmtWhen(task.createdAt)}</dd>
          </dl>

          {isOpen && canEdit && (
            <div className="tasks-actions">
              {task.status !== 'in_progress' && (
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => move('in_progress')}>Взяти в роботу</button>
              )}
              {task.status !== 'waiting' && (
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => move('waiting')}>Очікує клієнта</button>
              )}
              <button className="btn btn-success btn-sm" disabled={busy} onClick={() => setClosing(true)}>Виконано</button>
              <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setMoving(true)}>Перенести</button>
              {!task.blocked && (
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setBlocking(true)}>Блокує…</button>
              )}
              <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => setCancelling(true)}>Скасувати</button>
              {onEdit && (
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onEdit}>Редагувати</button>
              )}
            </div>
          )}

          {!!task.checklist.length && (
            <div className="tasks-checklist">
              <div className="tasks-section-title">Чекліст</div>
              {task.checklist.map((c, i) => (
                <label key={c.id || i} className="tasks-check">
                  <input
                    type="checkbox" checked={!!c.isDone} disabled={busy || !isOpen || !canEdit}
                    onChange={() => toggleCheck(i)}
                  />
                  <span className={c.isDone ? 'done' : ''}>{c.title}</span>
                </label>
              ))}
            </div>
          )}

          <div className="tasks-section-title tasks-section-title--row">
            <span>Файли{task.files?.length ? ` · ${task.files.length}` : ''}</span>
            <button
              type="button" className="btn btn-secondary btn-sm"
              disabled={busy || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Завантажую…' : '📎 Прикріпити'}
            </button>
            <input
              ref={fileInputRef} type="file" style={{ display: 'none' }}
              onChange={e => addFile(e.target.files?.[0])}
            />
          </div>
          {!!task.files?.length && (
            <div className="tasks-files">
              {task.files.map(f => (
                <div key={f.id} className="tasks-file">
                  <a href={f.url} target="_blank" rel="noopener noreferrer" className="tasks-file-name" title={f.name}>
                    {f.name}
                  </a>
                  <span className="tasks-file-meta">
                    {fmtSize(f.size)}{f.byName ? ` · ${f.byName}` : ''}{f.at ? ` · ${fmtWhen(f.at)}` : ''}
                  </span>
                  {(f.byUid === currentUser.uid || hasPermission('tasks_edit_all')) && (
                    <button type="button" className="tasks-file-remove" disabled={busy} title="Прибрати" onClick={() => removeFile(f)}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="tasks-section-title">Історія</div>
          <div className="tasks-comments">
            {needsHistory && <div className="tasks-empty-note">Завантажую історію…</div>}
            {!needsHistory && task.comments.length === 0 && <div className="tasks-empty-note">Поки нічого не відбувалось</div>}
            {(task.comments || []).map((c, i) => (
              <div key={c.id || i} className={`tasks-comment${c.isSystem ? ' system' : ''}`}>
                <div className="tasks-comment-head">
                  <span>{c.isSystem ? 'Система' : (c.authorName || 'Користувач')}</span>
                  <span>{fmtWhen(c.at)}</span>
                </div>
                <div className="tasks-comment-body">{c.body}</div>
              </div>
            ))}
          </div>

          <div className="tasks-comment-form">
            <textarea
              rows={2} value={comment} placeholder="Додати коментар…"
              onChange={e => setComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addComment(); }}
            />
            <button className="btn btn-primary btn-sm" disabled={busy || !comment.trim()} onClick={addComment}>
              Додати
            </button>
          </div>
        </div>
      </aside>

      {closing && (
        <CompleteTaskModal
          task={task}
          onClose={() => setClosing(false)}
          // Продовження (наступний екземпляр, «передзвонити») — новий рядок
          // у списку, який патчем не дістати: перечитуємо.
          onDone={(t, followUp) => { setClosing(false); onPatch(t); if (followUp && onReload) onReload(); }}
        />
      )}

      {moving && (
        <RescheduleModal
          task={task}
          onClose={() => setMoving(false)}
          onDone={(t) => { setMoving(false); onPatch(t); }}
        />
      )}

      {cancelling && (
        <CancelTaskModal
          task={task}
          onClose={() => setCancelling(false)}
          onDone={(t) => { setCancelling(false); onPatch(t); }}
        />
      )}

      {blocking && (
        <BlockTaskModal
          task={task}
          onClose={() => setBlocking(false)}
          onDone={(t) => { setBlocking(false); onPatch(t); }}
        />
      )}
    </>
  );
}

function RescheduleRequestBanner({ task, busy, onAnswer }) {
  const { currentUser, hasPermission } = useAuth();
  const r = task.rescheduleRequest;
  const canAnswer = task.createdBy?.uid === currentUser.uid || hasPermission('tasks_edit_all');

  return (
    <div className="task-request">
      <div className="task-request-head">
        <strong>Запит на перенесення</strong>
        <span>{fmtWhen(r.at)}</span>
      </div>
      <div className="task-request-body">
        {r.byName || 'Виконавець'} просить перенести на <strong>{fmtWhen(r.dueAt)}</strong>
        {r.reasonName ? ` · ${r.reasonName}` : ''}
        {r.comment ? <div className="task-request-note">{r.comment}</div> : null}
      </div>
      {canAnswer ? (
        <div className="task-request-actions">
          <button className="btn btn-success btn-sm" disabled={busy} onClick={() => onAnswer('approve')}>
            Підтвердити
          </button>
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onAnswer('reject')}>
            Відхилити
          </button>
        </div>
      ) : (
        <div className="task-request-note">Очікує відповіді постановника — дедлайн поки не змінено.</div>
      )}
    </div>
  );
}

function BlockedBanner({ task, busy, isBlocker, canRelease, onRelease }) {
  const b = task.blocked;
  const who = b.byName || b.external || 'когось';
  const expectedPassed = b.expectedAt && b.expectedAt < Date.now();

  return (
    <div className="task-request task-request--blocked">
      <div className="task-request-head">
        <strong>Блокує {who}{b.external ? ' (зовнішній)' : ''}</strong>
        <span>з {fmtWhen(b.since)}</span>
      </div>
      <div className="task-request-body">
        {b.what}
        <div className="task-request-note">
          Очікуване розблокування: {fmtWhen(b.expectedAt)}
          {expectedPassed ? ' — термін минув' : ''}
          {b.setByName ? ` · позначив ${b.setByName}` : ''}
        </div>
      </div>
      {canRelease && (
        <div className="task-request-actions">
          <button className="btn btn-success btn-sm" disabled={busy} onClick={onRelease}>
            {isBlocker ? 'Я зробив' : 'Зняти блокування'}
          </button>
        </div>
      )}
    </div>
  );
}
